import {Injectable, inject} from '@angular/core'
import {Actions, createEffect, ofType} from '@ngrx/effects'
import {forkJoin, of} from 'rxjs'
import {catchError, map, mergeMap, switchMap} from 'rxjs/operators'
import {ToastNotificationService} from '@common/components/toast-notification/toast-notification.service'
import {Action} from '@models/action'
import {Appointment} from '@models/appointment'
import {UveLogKey} from '@models/constants/log-key'
import {Distributor} from '@models/distributor'
import {Order} from '@models/order/order'
import {Outlet} from '@models/outlet'
import {OutletRelationCode} from '@models/outletRelationCode'
import {Hierarchy, SalesInfo, SalesResponse} from '@models/sales'
import {setOutletsListByAppointment, updateOutletOnList} from '@pages/home/store/home.actions'
import {getAppointmentsResponse, updateScheduleAppointmentResponse} from '@pages/schedule/store/schedule.actions'
import {ActionService} from '@service/actions/action.service'
import {AppointmentService} from '@service/appointment.service'
import {AssortmentsService} from '@service/assortments.service'
import {AuthService} from '@service/auth/auth.service'
import {DistributorService} from '@service/distributors/distributor.service'
import {LoggingService} from '@service/logging.service'
import {OrderService} from '@service/orders/order/order.service'
import {OutletService} from '@service/outlet/outlet.service'
import {OutletDetailService} from '@service/outlet-detail.service'
import {SalesService} from '@service/sales.service'
import {TranslationServicex} from '@service/translationx.service'
import {setNextAppointment} from './actitivies/activities.actions'
import {
  OutletDetailsActionsTypes,
  createNextAppointmentResponse,
  getDistributorsResponse,
  getOutletOrderInfoResponse,
  getSalesResponse,
  updateOutletResponse,
} from './outlet-details.actions'

@Injectable()
export class OutletDetailsEffects {
  private readonly actions$ = inject(Actions)
  private readonly actionService = inject(ActionService)
  private readonly appointmentService = inject(AppointmentService)
  private readonly salesService = inject(SalesService)
  private readonly orderService = inject(OrderService)
  private readonly assortmentsService = inject(AssortmentsService)
  private readonly distributorService = inject(DistributorService)
  private readonly outletService = inject(OutletService)
  private readonly loggingService = inject(LoggingService)
  private readonly translationServicex = inject(TranslationServicex)
  private readonly toastNotificationService = inject(ToastNotificationService)
  private readonly outletDetailService = inject(OutletDetailService)
  private readonly authService = inject(AuthService)

  salesOutlet$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OutletDetailsActionsTypes.SALES_REQUEST),
      switchMap(({outletId}: {outletId: string}) =>
        forkJoin([this.salesService.getOutletSales(outletId), this.salesService.getHierarchyLevels()])
      ),
      switchMap(([sales, hierarchyLevels]: [SalesResponse, Hierarchy[]]) =>
        of(this.salesService.constructSalesData(sales, hierarchyLevels))
      ),
      map((sales: SalesInfo) => getSalesResponse({sales}))
    )
  )

  createNewAppointment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OutletDetailsActionsTypes.CREATE_NEXT_APPOINTMENT_REQUEST),
      switchMap(({outletId, date, appointmentType, appointmentNotes, objectives, duration, userId, name}) =>
        forkJoin({
          appointment: this.appointmentService.createAppointment({
            outletId,
            date,
            type: appointmentType,
            notes: appointmentNotes,
            goals: objectives,
            duration,
            userId,
            name,
          }),
          outlet: this.outletService.getOutlet(outletId),
        })
      ),
      mergeMap(({appointment, outlet}: {appointment: Appointment; outlet: Outlet}) => {
        const appoinmentData = {
          ...appointment,
          outletInfo: {
            ...outlet,
            appointmentDate: appointment.appointmentDate,
            appointmentInfo: appointment,
          },
        }
        return [
          setOutletsListByAppointment({
            outlet: this.outletService.mapOutlet(appoinmentData.outletInfo),
            appointment: appoinmentData,
            appointmentOld: null,
          }),
          setNextAppointment({
            nextAppointment: appointment,
          }),
          createNextAppointmentResponse(),
          getAppointmentsResponse({
            appointments: [appoinmentData],
          }),
        ]
      })
    )
  )

  updateAppointment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OutletDetailsActionsTypes.UPDATE_NEXT_APPOINTMENT_REQUEST),
      switchMap(
        ({
          outletId,
          oldAppointment,
          appointment,
        }: {
          outletId: string
          oldAppointment: Appointment
          appointment: Appointment
        }) =>
          forkJoin({
            outlet: this.outletService.getOutlet(outletId),
            appointment: of(appointment),
            appointmentOld: of(oldAppointment),
          })
      ),
      switchMap(
        ({
          outlet,
          appointment,
          appointmentOld,
        }: {
          outlet: Outlet
          appointment: Appointment
          appointmentOld: Appointment
          result: boolean
        }) =>
          forkJoin({
            outlet: of(outlet),
            appointment: this.appointmentService.updateAppointment(appointment),
            appointmentOld: of(appointmentOld),
          })
      ),
      switchMap(
        ({
          outlet,
          appointment,
          appointmentOld,
        }: {
          outlet: Outlet
          appointment: Appointment
          appointmentOld: Appointment
          result: boolean
        }) =>
          forkJoin({
            outlet: of(outlet),
            appointment: of(appointment),
            appointmentOld: of(appointmentOld),
            nextAppointment: this.appointmentService.getNextAppointment(outlet.id),
          })
      ),
      mergeMap(
        ({
          outlet,
          appointment,
          appointmentOld,
          nextAppointment,
        }: {
          outlet: Outlet
          appointment: Appointment
          appointmentOld: Appointment
          nextAppointment: Appointment[]
        }) => {
          appointment = {
            ...appointment,
            appointmentDuration: appointment.appointmentDuration / 60,
          }
          const appointmentData = {
            ...appointment,
            outletInfo: {
              ...outlet,
              appointmentDate: appointment.appointmentDate,
              appointmentInfo: appointment,
            },
          }

          this.loggingService.debug(UveLogKey.Debug.UpdateAppointment, {
            extra: JSON.stringify({
              outletId: outlet.id,
              appointmentId: appointmentData.appointmentId,
            }),
          })

          const actions = [
            updateScheduleAppointmentResponse({
              appointment: appointmentData,
            }),
            setOutletsListByAppointment({
              outlet: this.outletService.mapOutlet(appointmentData.outletInfo),
              appointment: appointmentData.user === this.authService.getUserName() ? appointmentData : null,
              appointmentOld,
            }),
            getAppointmentsResponse({
              appointments: [appointmentData],
            }),
          ]

          if (nextAppointment.length > 0 && nextAppointment[0].appointmentId === appointmentData.appointmentId) {
            return [
              ...actions,
              setNextAppointment({
                nextAppointment: appointmentData,
              }),
            ]
          }

          return actions
        }
      )
    )
  )

  getDistributors$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OutletDetailsActionsTypes.DISTRIBUTORS_REQUEST),
      switchMap(() => this.distributorService.getDistributors()),
      map((distributors: Distributor[]) => getDistributorsResponse({distributors})),
      catchError(() => {
        this.toastNotificationService.error(this.translationServicex.instant('error_saving_order'))

        return of({type: ''})
      })
    )
  )

  updateOutlet$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OutletDetailsActionsTypes.UPDATE_OUTLET_REQUEST),
      switchMap(({outlet}) => this.outletService.updateOutlet(outlet)),
      mergeMap((outlet: Outlet) => {
        this.outletDetailService.data.loadedOutletId = ''
        this.outletDetailService.loadingOutlet = ''
        this.outletDetailService.data = {
          loadedOutletId: '',
          groups: [],
          productsAssortments: null,
          orders: [],
          codes: [],
          distributors: [],
          assortmentsAxis: [],
        }
        return [updateOutletOnList({outlet}), updateOutletResponse({outlet})]
      })
    )
  )

  getOutletOrderInfoCodes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OutletDetailsActionsTypes.PREPARE_ORDER_OUTLET_INFO_REQUEST),
      switchMap(
        ({
          outletId,
          outlet,
          actionId,
          orderId,
        }: {
          outletId: string
          outlet: Outlet
          actionId: string
          orderId: string
        }) =>
          forkJoin({
            outlet: outlet ? of(outlet) : this.outletService.getOutlet(outletId),
            codes: of(this.outletService.getOutletRelationCodes(outletId)),
            distributors: this.distributorService.getDistributors(),
            action: this.actionService.getAction(actionId, outletId, null),
            order: this.orderService.getOrder(orderId),
            productsAssortments: this.assortmentsService.getOutletSalesAssortments(outletId),
            pricelists: of([]),
          })
      ),
      map(
        ({
          outlet,
          codes,
          distributors,
          action,
          order,
          productsAssortments,
        }: {
          outlet: Outlet
          codes: OutletRelationCode[]
          distributors: Distributor[]
          action: Action
          order: Order
          productsAssortments: {
            excludeAssortments: any[]
            saleAssortments: any[]
          }
          pricelists: any[]
        }) =>
          getOutletOrderInfoResponse({
            outlet,
            codes,
            distributors,
            action,
            order,
            productsAssortments,
          })
      )
    )
  )
}
